(() => {
  'use strict';

  const root = window.KazeTheme?.routes?.root || '/';
  const ui = window.KazeTheme?.translations || {};
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const escapeHTML = (value) => String(value ?? '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char]);

  function formatMoney(cents) {
    const format = window.KazeTheme?.moneyFormat || 'KWD {{amount}}';
    const amount = (Number(cents || 0) / 100).toFixed(3);
    return format.replace(/\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator|amount_no_decimals_with_comma_separator)\s*\}\}/, amount);
  }

  function toast(message) {
    const element = qs('[data-toast]');
    if (!element) return;
    element.textContent = message;
    element.classList.add('is-visible');
    clearTimeout(element._timer);
    element._timer = setTimeout(() => element.classList.remove('is-visible'), 2800);
  }

  const drawer = qs('[data-cart-drawer]');
  const syncBodyLock = () => document.body.classList.toggle('is-locked', Boolean(drawer?.classList.contains('is-open') || qs('[data-mobile-nav].is-open')));

  function openMobileNav(nav) {
    if (!nav) return;
    qsa('[data-mobile-nav].is-open').forEach((other) => { if (other !== nav) closeMobileNav(other, false); });
    nav.classList.add('is-open');
    qs('[data-nav-open]', nav)?.setAttribute('aria-expanded', 'true');
    const panel = qs('.mobile-nav__panel', nav);
    panel?.setAttribute('aria-hidden', 'false');
    panel?.removeAttribute('inert');
    syncBodyLock();
    requestAnimationFrame(() => qs('[data-nav-close]', panel)?.focus());
  }

  function closeMobileNav(nav, restoreFocus = true) {
    if (!nav) return;
    nav.classList.remove('is-open');
    const trigger = qs('[data-nav-open]', nav);
    trigger?.setAttribute('aria-expanded', 'false');
    const panel = qs('.mobile-nav__panel', nav);
    panel?.setAttribute('aria-hidden', 'true');
    panel?.setAttribute('inert', '');
    syncBodyLock();
    if (restoreFocus) trigger?.focus();
  }

  function openCart() {
    if (!drawer) return;
    qsa('[data-mobile-nav].is-open').forEach((nav) => closeMobileNav(nav, false));
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    syncBodyLock();
    qs('[data-cart-close]', drawer)?.focus();
  }

  function closeCart() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    syncBodyLock();
  }

  function cartItemMarkup(item, line) {
    const fallbackTemplate = window.KazeTheme?.productFallbackTemplate || '';
    const itemHandle = item.handle || String(item.url || '').match(/\/products\/([^/?]+)/)?.[1] || '';
    const sourceId = itemHandle.match(/-(\d+)$/)?.[1] || '';
    const fallbackImage = sourceId && fallbackTemplate
      ? fallbackTemplate.replace('kaze-p-1.webp', `kaze-p-${sourceId}.webp`)
      : '';
    const image = item.featured_image?.url || item.image || fallbackImage;
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<small>${escapeHTML(item.variant_title)}</small>` : '';
    const imageUrl = image ? `${image}${image.includes('?') ? '&' : '?'}width=180` : '';
    return `<div class="drawer-item" data-line="${line}">
      ${image ? `<img class="drawer-item__image" src="${escapeHTML(imageUrl)}" width="88" height="100" alt="">` : ''}
      <div class="drawer-item__info"><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>${variant}<span>${formatMoney(item.final_line_price)}</span></div>
      <button type="button" class="text-button" data-cart-remove="${line}">${escapeHTML(ui.remove || 'Remove')}</button>
    </div>`;
  }

  function renderCart(cart) {
    qsa('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    });
    const items = qs('[data-cart-items]');
    const footer = qs('[data-cart-footer]');
    if (!items) return;
    if (!cart.item_count) {
      items.innerHTML = `<div class="empty-state"><span class="empty-state__icon">☕</span><h3>${escapeHTML(ui.cartEmpty || 'Your cart is empty')}</h3><p>${escapeHTML(ui.cartEmptyText || 'Discover your next daily ritual.')}</p><a href="${escapeHTML(root)}collections/all" class="button">${escapeHTML(ui.exploreShop || 'Explore the shop')}</a></div>`;
      if (footer) footer.hidden = true;
      return;
    }
    items.innerHTML = cart.items.map((item, index) => cartItemMarkup(item, index + 1)).join('');
    if (footer) footer.hidden = false;
    const total = qs('[data-cart-total]');
    if (total) total.textContent = formatMoney(cart.total_price);
  }

  async function getCart() {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart could not be loaded.');
    return response.json();
  }

  async function addToCart(form) {
    const button = qs('.product-add, .quick-add', form);
    const original = button?.innerHTML;
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'Could not add this item.');
      renderCart(await getCart());
      openCart();
      toast(ui.addedToCart || 'Added to your cart');
    } catch (error) {
      toast(error.message || 'Something went wrong.');
    } finally {
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); button.innerHTML = original; }
    }
  }

  async function removeCartLine(line) {
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: 0 })
      });
      if (!response.ok) throw new Error('Could not update cart.');
      renderCart(await response.json());
    } catch (error) { toast(error.message); }
  }

  function initCart() {
    qsa('[data-cart-open]').forEach((button) => button.addEventListener('click', (event) => { event.preventDefault(); openCart(); }));
    qsa('[data-cart-close]').forEach((button) => button.addEventListener('click', closeCart));
    document.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) removeCartLine(remove.dataset.cartRemove);
    });
    document.addEventListener('submit', (event) => {
      const form = event.target.closest('.js-product-form');
      if (!form) return;
      if (event.submitter && !event.submitter.matches('.product-add, .quick-add')) return;
      event.preventDefault();
      addToCart(form);
    });
  }

  function initProduct() {
    qsa('[data-product-form]').forEach((form) => {
      const json = qs('[data-product-json]', form);
      if (!json) return;
      let product;
      try { product = JSON.parse(json.textContent); } catch (_) { return; }
      const idInput = qs('[name="id"]', form);
      const price = qs('[data-product-price]');
      const submit = qs('.product-add', form);
      form.addEventListener('change', () => {
        const options = qsa('[data-option]:checked', form).map((input) => input.value);
        const variant = product.variants.find((candidate) => candidate.options.every((option, index) => option === options[index]));
        if (!variant) return;
        idInput.value = variant.id;
        if (price) price.textContent = formatMoney(variant.price);
        if (submit) {
          submit.disabled = !variant.available;
          const label = qs('span', submit);
          if (label) label.textContent = variant.available ? (ui.addToCart || 'Add to cart') : (ui.soldOut || 'Sold out');
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      });
    });
  }

  function initProductGallery() {
    qsa('[data-product-gallery]').forEach((gallery) => {
      qsa('[data-product-thumb]', gallery).forEach((thumb) => thumb.addEventListener('click', () => {
        const mediaId = thumb.dataset.productThumb;
        qsa('[data-product-media]', gallery).forEach((media) => { media.hidden = media.dataset.productMedia !== mediaId; });
        qsa('[data-product-thumb]', gallery).forEach((candidate) => {
          const active = candidate === thumb;
          candidate.classList.toggle('is-active', active);
          candidate.setAttribute('aria-pressed', String(active));
        });
      }));
    });
  }

  function initQuantity() {
    qsa('[data-quantity]').forEach((wrap) => {
      const input = qs('input', wrap);
      qsa('button', wrap).forEach((button) => button.addEventListener('click', () => {
        const next = Math.max(Number(input.min || 1), Number(input.value || 1) + Number(button.dataset.step));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    });
  }

  function initMenus() {
    qsa('[data-mobile-nav]').forEach((nav) => {
      qs('[data-nav-open]', nav)?.addEventListener('click', () => openMobileNav(nav));
      qsa('[data-nav-close]', nav).forEach((button) => button.addEventListener('click', () => closeMobileNav(nav, !button.classList.contains('mobile-nav__overlay'))));
      qsa('a', nav).forEach((link) => link.addEventListener('click', () => closeMobileNav(nav, false)));
    });

    const desktopMenu = matchMedia('(min-width: 991px)');
    desktopMenu.addEventListener?.('change', (event) => { if (event.matches) qsa('[data-mobile-nav].is-open').forEach((nav) => closeMobileNav(nav, false)); });

    const mobileFooter = matchMedia('(max-width: 749px)');
    const syncFooter = () => qsa('[data-footer-accordion]').forEach((details) => { details.open = !mobileFooter.matches; });
    syncFooter();
    mobileFooter.addEventListener?.('change', syncFooter);

    qsa('[data-sort]').forEach((select) => select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.assign(url.toString());
    }));
  }

  function initLanguageSwitchers() {
    qsa('[data-language-select]').forEach((select) => {
      if (select.dataset.languageBound) return;
      select.dataset.languageBound = 'true';
      select.addEventListener('change', () => {
        const form = select.form;
        if (!form || select.disabled) return;
        form.classList.add('is-loading');
        form.setAttribute('aria-busy', 'true');
        select.disabled = true;
        form.submit();
      });
    });
  }

  function collectCartAttributes(form) {
    const attributes = {};
    qsa('[name^="attributes["]', form).forEach((input) => {
      if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;
      const match = input.name.match(/^attributes\[(.+)\]$/);
      if (match) attributes[match[1]] = input.value;
    });
    return attributes;
  }

  function normalizeWhatsAppConsent(form, options = {}) {
    const phoneInput = qs('[data-whatsapp-phone]', form);
    const updatesInput = qs('[data-whatsapp-order-updates]', form);
    const offersInput = qs('[data-whatsapp-offers]', form);
    const consentAt = qs('[data-whatsapp-consent-at]', form);
    const hasConsent = Boolean(updatesInput?.checked || offersInput?.checked);
    const phone = phoneInput?.value.trim() || '';

    if (hasConsent && !phone) {
      phoneInput?.focus();
      toast('Add your WhatsApp number to receive updates.');
      return false;
    }

    if (hasConsent) {
      if (consentAt && !consentAt.value) consentAt.value = new Date().toISOString();
    } else if (options.clearPhone !== false) {
      // Do not send or store a phone number unless the customer has opted in.
      if (phoneInput) phoneInput.value = '';
      if (consentAt) consentAt.value = '';
    } else if (consentAt) {
      consentAt.value = '';
    }
    return true;
  }

  let whatsappSyncTimer;

  async function syncWhatsAppCartAttributes(form) {
    if (!form.matches('[data-cart-page-form], [data-checkout-form]')) return;
    if (!normalizeWhatsAppConsent(form, { clearPhone: false })) return;
    const attributes = collectCartAttributes(form);
    const updatesInput = qs('[data-whatsapp-order-updates]', form);
    const offersInput = qs('[data-whatsapp-offers]', form);
    const phoneInput = qs('[data-whatsapp-phone]', form);
    const consentAt = qs('[data-whatsapp-consent-at]', form);
    const hasConsent = Boolean(updatesInput?.checked || offersInput?.checked);
    attributes['WhatsApp phone'] = hasConsent ? (phoneInput?.value.trim() || '') : '';
    attributes['WhatsApp order updates'] = updatesInput?.checked ? 'yes' : '';
    attributes['WhatsApp offers'] = offersInput?.checked ? 'yes' : '';
    attributes['WhatsApp consent at'] = hasConsent ? (consentAt?.value || new Date().toISOString()) : '';
    attributes['WhatsApp consent source'] = hasConsent ? 'Kaze theme' : '';
    try {
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes })
      });
    } catch (_) {
      // A failed background sync must never block checkout; the native form still carries the fields.
    }
  }

  function scheduleWhatsAppCartSync(form) {
    clearTimeout(whatsappSyncTimer);
    whatsappSyncTimer = setTimeout(() => syncWhatsAppCartAttributes(form), 220);
  }

  async function submitWhatsAppSignup(form) {
    const message = qs('[data-whatsapp-message]', form);
    const button = qs('[data-whatsapp-submit]', form);
    const endpoint = form.getAttribute('action') || window.KazeTheme?.whatsapp?.endpoint || '';
    if (!endpoint) {
      if (message) message.textContent = 'WhatsApp signup is not connected yet. Add your secure endpoint in Theme settings.';
      return;
    }

    const values = Object.fromEntries(new FormData(form).entries());
    const phone = String(values.phone || '').trim();
    if (!phone || values.offers_opt_in !== 'yes') {
      if (message) message.textContent = 'Enter a WhatsApp number and confirm the opt-in to continue.';
      return;
    }

    values.phone = phone;
    values.shop = values.shop || window.location.hostname;
    values.return_url = values.return_url || window.location.href;
    values.order_link = window.KazeTheme?.whatsapp?.orderLink || `${window.location.origin}${root}`;
    values.business_name = window.KazeTheme?.whatsapp?.businessName || '';
    values.order_template = window.KazeTheme?.whatsapp?.orderTemplate || '';
    values.winback_template = window.KazeTheme?.whatsapp?.winbackTemplate || '';

    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    if (message) message.textContent = 'Saving your WhatsApp preference…';
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'omit',
        body: JSON.stringify(values)
      });
      let payload = {};
      if (response.headers.get('content-type')?.includes('application/json')) payload = await response.json();
      if (!response.ok) throw new Error(payload.message || 'We could not save that preference. Please try again.');
      if (message) message.textContent = payload.message || 'You’re subscribed. We’ll message you on WhatsApp when there’s something useful to share.';
      form.reset();
    } catch (error) {
      if (message) message.textContent = error.message || 'We could not save that preference. Please try again.';
    } finally {
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
    }
  }

  function initWhatsApp() {
    const consentForms = new Set(qsa('[data-whatsapp-consent]').map((block) => block.closest('form')).filter(Boolean));
    consentForms.forEach((form) => {
      if (form.dataset.whatsappConsentBound) return;
      form.dataset.whatsappConsentBound = 'true';
      qsa('[data-whatsapp-phone], [data-whatsapp-order-updates], [data-whatsapp-offers]', form).forEach((input) => {
        input.addEventListener('change', () => scheduleWhatsAppCartSync(form));
        input.addEventListener('blur', () => scheduleWhatsAppCartSync(form));
      });
      form.addEventListener('submit', (event) => {
        if (!normalizeWhatsAppConsent(form)) event.preventDefault();
      });
    });

    qsa('[data-whatsapp-signup-form]').forEach((form) => {
      if (form.dataset.whatsappSignupBound) return;
      form.dataset.whatsappSignupBound = 'true';
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        submitWhatsAppSignup(form);
      });
    });
  }

  async function saveCartForm(form) {
    if (!normalizeWhatsAppConsent(form)) throw new Error('Add a WhatsApp number or clear the WhatsApp opt-in.');
    const updates = {};
    qsa('[data-line-key]', form).forEach((input) => { updates[input.dataset.lineKey] = Number(input.value || 0); });
    const payload = { attributes: collectCartAttributes(form) };
    if (Object.keys(updates).length) payload.updates = updates;
    const note = qs('[name="note"]', form);
    if (note) payload.note = note.value;
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error('Your cart could not be updated.');
    return response.json();
  }

  function initCheckoutFlow() {
    qsa('[data-apply-discount]').forEach((button) => button.addEventListener('click', () => {
      const field = qs('[data-discount-code]', button.closest('form') || document);
      if (!field?.value.trim()) { field?.focus(); toast('Enter a discount code first.'); return; }
      button.textContent = 'Ready';
      toast('Code will be applied securely at checkout.');
    }));

    qs('[data-clear-cart]')?.addEventListener('click', async () => {
      try {
        const response = await fetch(`${root}cart/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Cart could not be cleared.');
        window.location.reload();
      } catch (error) { toast(error.message); }
    });

    qsa('[data-cart-handoff]').forEach((button) => button.addEventListener('click', async () => {
      const form = button.closest('form');
      button.disabled = true;
      try {
        await saveCartForm(form);
        window.location.assign(button.dataset.cartHandoff);
      } catch (error) { button.disabled = false; toast(error.message); }
    }));

    qsa('[data-checkout-form], [data-cart-page-form]').forEach((form) => form.addEventListener('submit', async (event) => {
      const submitter = event.submitter;
      if (!submitter?.matches('[data-checkout-button]')) return;
      const code = qs('[data-discount-code]', form)?.value.trim();
      if (!code) return;
      event.preventDefault();
      submitter.disabled = true;
      try {
        await saveCartForm(form);
        const nativeCheckout = window.KazeTheme?.routes?.nativeCheckout || `${root}checkout`;
        window.location.assign(`${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(nativeCheckout)}`);
      } catch (error) { submitter.disabled = false; toast(error.message); }
    }));
  }

  function initKeyboardControls() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (drawer?.classList.contains('is-open')) closeCart();
        qsa('[data-mobile-nav].is-open').forEach((nav) => closeMobileNav(nav));
      }
      if (event.key !== 'Tab') return;
      const nav = qs('[data-mobile-nav].is-open');
      if (!nav) return;
      const panel = qs('.mobile-nav__panel', nav);
      const focusable = qsa('a[href],button:not([disabled]),input:not([disabled])', panel);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  }

  function initQuoteContact() {
    const form = qs('#KazeContactForm');
    if (!form || form.dataset.quoteReady === 'true') return;
    form.dataset.quoteReady = 'true';

    const productTitle = new URLSearchParams(window.location.search).get('product')?.trim();
    if (!productTitle) return;

    const productInput = qs('[data-quote-product]', form);
    const message = qs('[data-quote-message]', form);
    const notice = qs('[data-quote-notice]', form);
    const noticeTitle = qs('[data-quote-title]', form);
    if (productInput) productInput.value = productTitle;
    if (message && !message.value.trim()) {
      message.value = `Hello Kaze Cafe,\n\nI would like to request a quote for ${productTitle}.\n\nPlease share current availability and pricing.`;
    }
    if (noticeTitle) noticeTitle.textContent = productTitle;
    if (notice) notice.hidden = false;
  }

  function initAnimations() {
    if (!window.KazeTheme?.animations || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.gsap || !window.ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);
    gsap.utils.toArray('[data-reveal]').forEach((element) => {
      gsap.fromTo(element,
        { autoAlpha: 0, y: 44 },
        { autoAlpha: 1, y: 0, duration: .85, ease: 'power3.out', scrollTrigger: { trigger: element, start: 'top 88%', end: 'bottom 12%', toggleActions: 'play none none reverse' } }
      );
    });
    gsap.utils.toArray('[data-stagger]').forEach((group) => {
      const children = [...group.children];
      gsap.fromTo(children,
        { autoAlpha: 0, y: 36 },
        { autoAlpha: 1, y: 0, duration: .7, stagger: .09, ease: 'power2.out', scrollTrigger: { trigger: group, start: 'top 86%', end: 'bottom 10%', toggleActions: 'play none none reverse' } }
      );
    });
    gsap.utils.toArray('[data-parallax]').forEach((media) => {
      gsap.fromTo(media, { yPercent: -4, scale: 1.04 }, { yPercent: 4, ease: 'none', scrollTrigger: { trigger: media.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } });
    });
    window.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initCart();
    initProduct();
    initProductGallery();
    initQuantity();
    initMenus();
    initLanguageSwitchers();
    initWhatsApp();
    initCheckoutFlow();
    initKeyboardControls();
    initQuoteContact();
    initAnimations();
  });

  document.addEventListener('shopify:section:load', () => {
    initLanguageSwitchers();
    initWhatsApp();
    initQuoteContact();
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });
})();
